// Display names for the German values the budget data stores. The stored value
// stays the key everywhere — filters, embed URLs, colours, stack order, the
// Haushaltstechnik split — and only what a reader sees changes. Unknown values
// fall back to the German text, so a new category never renders blank.
//
// The translations themselves are data (i18n.translations, loaded by data.ts;
// see docs/i18n.md). These helpers only know the formats the API prints the
// names in. Budget-title texts (Zweckbestimmung) stay in the official German.
import { dataLabel } from './data';

/** "Verwaltungskosten" → "Administrative costs" in English; unchanged in German
    or when unknown. Covers main categories, subcategories and the API's
    synthetic "Sonstiges" / "Nicht zugeordnet" buckets. */
export function categoryLabel(value: string): string {
  return dataLabel('budget.category', value);
}

/** An Einzelplan's name (einzelplan_text) in the widget language. */
export function einzelplanName(text: string): string {
  return dataLabel('budget.einzelplan', text);
}

/** A "<code> - <name>" Einzelplan label (the filter tree's format). */
export function einzelplanLabel(label: string): string {
  const sep = label.indexOf(' - ');
  if (sep <= 0) return einzelplanName(label);
  return `${label.slice(0, sep)} - ${einzelplanName(label.slice(sep + 3))}`;
}

export type LookupFamily = 'gruppierung' | 'funktion' | 'kapitel';

// The forms the API prints these names in: "<code> - <name>" (Gruppierung,
// Funktion), "<ep>|<kap> <name>" (Kapitel options) or the bare name; a
// chapter whose code was reused carries a " (<year>)" or " (<from>-<to>)"
// suffix (processKapitelRows, apps/api/src/budget/db.ts).
const LOOKUP_LABEL = /^(\d+ - |\d+\|\d+ )?([\s\S]*?)( \(\d{4}(?:-\d{4})?\))?$/;

/** A Gruppierung / Funktion / Kapitel label in the widget language: the code
    and any year suffix are kept, the name is translated. Unknown names (and
    every name in German) come back unchanged. */
export function lookupLabel(family: LookupFamily, label: string): string {
  const m = LOOKUP_LABEL.exec(label);
  if (!m) return label;
  const name = m[2]!;
  const translated = dataLabel(`budget.${family}`, name);
  return translated === name ? label : `${m[1] ?? ''}${translated}${m[3] ?? ''}`;
}
